import { Viewport } from '@cornerstonejs/core';
import { isReadonly, labelTask, tools } from '@gleamer/ui';
import { ServicesManager } from '@ohif/core';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getObservationColor,
  getSegmentKey,
  isBrushToolName,
} from '../../../utils/tools/brush';
import { getBindingsForTool } from '../../../utils/tools/mouse-bindings';
import { ensureMaskSegment } from '../../../utils/tools/maskSegmentation';

/**
 * In a task, the Brush paints in the Segment of the pending Observation. This
 * selects (or creates) that Segment whenever the brush is the task tool on the
 * active viewport, then activates the brush. Outside a task, the toolbar
 * commands do it instead.
 */
export function useMaskSegment({
  servicesManager,
  viewportId,
  viewport,
  isActiveViewport,
}: {
  servicesManager: ServicesManager;
  viewportId: string;
  viewport: Viewport | null;
  isActiveViewport: boolean;
}) {
  const currentTool = useSelector(tools.selectors.getTool);
  const nextObs = useSelector(tools.selectors.getNextObs);
  const obsSpecs = useSelector(
    labelTask.selectors.getObservationsSpecifications
  );

  const brushToolName =
    isActiveViewport && !isReadonly() && isBrushToolName(currentTool?.name)
      ? currentTool.name
      : null;
  const { code, status, index } = nextObs;

  useEffect(() => {
    if (!brushToolName || !viewport || !code) {
      return;
    }

    // Until the Segment is ready, a stroke would be dropped (no labelmap yet)
    // or painted in the previous Observation's Segment.
    const toolGroup =
      servicesManager.services.toolGroupService.getToolGroupForViewport(
        viewportId
      );
    toolGroup?.setToolPassive(brushToolName);

    const controller = new AbortController();
    const colors = obsSpecs.find(
      obs => obs.code === code && obs.status === status
    )?.colors;
    ensureMaskSegment(servicesManager, {
      viewportId,
      segmentKey: getSegmentKey({ code, status, index }),
      label: `${code} ${index}`,
      color: getObservationColor(colors, index),
      signal: controller.signal,
    })
      .then(isReady => {
        if (isReady && !controller.signal.aborted) {
          toolGroup?.setToolActive(brushToolName, {
            bindings: getBindingsForTool('Annotation'),
          });
        }
      })
      .catch(error =>
        console.error('Could not prepare the mask segment', error)
      );
    return () => controller.abort();
  }, [
    brushToolName,
    viewport,
    code,
    status,
    index,
    obsSpecs,
    servicesManager,
    viewportId,
  ]);
}
